'use client';

import { useEffect, useRef, useState } from 'react';
import { apiPaths } from '@/constants/apiPaths';
import { useCartStore } from '@/ui/store/cart';
import { useLocalization } from '@/ui/providers/localizationProvider';

export interface HydratedItemPrice {
  gross: number;
  net: number;
  currency: string;
  taxPercent: number;
  taxName: string;
}

export interface HydratedCartItem {
  sku: string;
  name: string;
  imageUrl?: string;
  quantity: number;
  unitPrice: HydratedItemPrice;
  lineTotal: HydratedItemPrice;
}

export interface HydratedCartTotal {
  gross: number;
  net: number;
  currency: string;
  taxAmount: number;
}

export interface HydratedCart {
  items: HydratedCartItem[];
  total: HydratedCartTotal;
  taxSource: 'crystallize' | 'taxjar';
  cartId: string;
}

const SESSION_STORAGE_POSTAL_CODE_KEY = 'checkoutPostalCode';
const SESSION_STORAGE_CART_ID_KEY = 'crystallizeCartId';

function extractSkuItems(
  products: Array<{ sku: string; quantity: number }>,
  bundles: Array<{ productVariants: Array<{ sku: string; quantity: number }>; quantity: number }>,
): Array<{ sku: string; quantity: number }> {
  const productItems = products.map((p) => ({ sku: p.sku, quantity: p.quantity }));

  const bundleItems = bundles.flatMap((bundle) =>
    bundle.productVariants.map((variant) => ({
      sku: variant.sku,
      quantity: variant.quantity * bundle.quantity,
    })),
  );

  return [...productItems, ...bundleItems];
}

function readPostalCodeFromSession(): string | undefined {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_POSTAL_CODE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function readCartIdFromSession(): string | undefined {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_CART_ID_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeCartIdToSession(cartId: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_CART_ID_KEY, cartId);
  } catch {
    // Silently swallow QuotaExceededError or SecurityError — non-critical
  }
}

function logTaxMismatch(clientTotal: number, shopApiTotal: number): void {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const diff = Math.abs(shopApiTotal - clientTotal);
  const TAX_MISMATCH_THRESHOLD = 0.01;

  if (diff > TAX_MISMATCH_THRESHOLD) {
    // eslint-disable-next-line no-console
    console.warn('[TAX MISMATCH] Crystallize Shop API total differs from client-side total:', {
      clientTotal: clientTotal.toFixed(2),
      shopApiTotal: shopApiTotal.toFixed(2),
      difference: diff.toFixed(2),
    });
  }
}

export interface UseCrystallizeCartHydrationResult {
  hydratedCart: HydratedCart | null;
  isLoading: boolean;
  error: Error | null;
}

export function useCrystallizeCartHydration(): UseCrystallizeCartHydrationResult {
  const {
    items, country, getTotalAfterDiscount, getPriceVariant,
  } = useCartStore();
  const { language } = useLocalization();

  // Ref keeps the latest getTotalAfterDiscount without making it a dep — it's
  // only used for dev-mode mismatch logging and must not trigger re-fetches.
  const getTotalRef = useRef(getTotalAfterDiscount);
  getTotalRef.current = getTotalAfterDiscount;

  const [hydratedCart, setHydratedCart] = useState<HydratedCart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const skuItems = extractSkuItems(items.products, items.bundles);

    if (skuItems.length === 0) {
      setHydratedCart(null);
      return;
    }

    const currency = getPriceVariant()?.currency ?? '';
    const postalCode = readPostalCodeFromSession();
    // Session key 'checkoutPostalCode' is written by the checkout address step
    const cartId = readCartIdFromSession();
    const controller = new AbortController();

    const hydrate = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(apiPaths.cart.hydrate, {
          signal: controller.signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: skuItems,
            language,
            currency,
            country: country ?? '',
            postalCode,
            cartId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Cart hydration failed with status ${response.status}`);
        }

        const result = await response.json();
        const cart: HydratedCart = result?.data;

        if (!cart) {
          throw new Error('Cart hydration returned empty data');
        }

        if (cart.cartId) {
          writeCartIdToSession(cart.cartId);
        }

        setHydratedCart(cart);

        logTaxMismatch(getTotalRef.current(), cart.total.gross);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        setError(err instanceof Error ? err : new Error('Cart hydration failed'));
      } finally {
        setIsLoading(false);
      }
    };

    hydrate();

    return () => controller.abort();
  }, [items, country, language, getPriceVariant]);

  return { hydratedCart, isLoading, error };
}
